import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { LanggraphChatProvider } from './context/LanggraphChatContext';
import LoginPage from './pages/LoginPage';
import ChatPage from './pages/ChatPage';
import ProfilePage from './pages/ProfilePage';
import PrivacyPage from './pages/PrivacyPage';

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, isEmailVerified } = useAuth();
  const { labels } = useLanguage();
  const [showSessionExpired, setShowSessionExpired] = React.useState(false);

  React.useEffect(() => {
    // Show session expired message if not authenticated after loading
    if (!isLoading && !isAuthenticated) {
      setShowSessionExpired(true);
    } else {
      setShowSessionExpired(false);
    }
  }, [isAuthenticated, isLoading]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="bg-error-50 text-error-700 p-4 rounded-md mb-4 text-center">
          {showSessionExpired ? labels.sessionExpiredMessage : labels.notAuthenticatedMessage}
        </div>
        <Navigate to="/login" replace />
      </div>
    );
  }

  if (!isEmailVerified) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="bg-warning-50 text-warning-700 p-4 rounded-md mb-4 text-center">
          {labels.verifyEmailAccessMessage}
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <LanggraphChatProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route 
                path="/chat" 
                element={
                  <ProtectedRoute>
                    <ChatPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />
              <Route path="*" element={<Navigate to="/chat" replace />} />
            </Routes>
          </LanggraphChatProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
